import { useEffect, useRef, useState } from "react";
import { ImageUp, Trash2 } from "lucide-react";
import { LOGO_MAX_BYTES, researchSettingsSchema, type Branding, type ResearchSettings, type StudyPages } from "@metrickle/schema";
import { PLATFORMS } from "@metrickle/schema/constants";
import { contrastRatio, WIDGET_BACKGROUNDS, WIDGET_DEFAULT_ACCENT } from "@metrickle/schema/tokens";
import { api, useAddons, useApiMutation, useResearch, type App } from "../lib/api";
import { PLATFORM_LABEL } from "../lib/format";
import { track } from "../lib/track";
import { Badge, Button, Card, CardHeader, CardSection, Checkbox, Field, FieldError, InlineCode, Input, Segmented, SelectField, notifyError, toaster, useConfirm } from "./ui";

/**
 * Research features for one app: the feedback widget, heatmaps and session replay. Saved settings
 * reach SDKs within about a minute through the edge-cached `/v1/config` endpoint.
 */
export function ResearchSettingsCard({ app, disabled }: { app: App; disabled: boolean }) {
  const q = useResearch(app.id);
  const addons = useAddons(app.id);
  const [s, setS] = useState<ResearchSettings | null>(null);
  // A background refetch must not throw away edits that haven't been saved yet.
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (q.data && !dirty) setS(q.data);
  }, [q.data, dirty]);
  const save = useApiMutation((body: ResearchSettings) => api<ResearchSettings>(`/apps/${app.id}/research`, { method: "PUT", json: body }), [["research", app.id]]);
  if (!s) return null;
  const web = app.platform === "web";
  const set = <K extends keyof ResearchSettings>(k: K, patch: Partial<ResearchSettings[K]>) => {
    setS({ ...s, [k]: { ...s[k], ...patch } });
    setDirty(true);
  };

  return (
    <Card>
      <CardHeader
        title="Research"
        subtitle={`Surveys are set up on the Surveys page. These switch on the feedback widget, heatmaps, replay and accessibility scans in visitors' browsers. ${web ? "The web SDK loads their code only when they're on." : "On native platforms, feedback and surveys are available through the SDK; heatmaps and replay are web-only for now."}`}
      />
      <CardSection>
        <form
          className="space-y-5"
          onSubmit={async (e) => {
            e.preventDefault();
            const parsed = researchSettingsSchema.safeParse(s);
            if (!parsed.success) return notifyError(new Error(parsed.error.issues[0]?.message ?? "Invalid settings"));
            try {
              setS(await save.mutateAsync(parsed.data));
              setDirty(false);
              track("research_settings_saved", { feedback: parsed.data.feedback.enabled, heatmaps: parsed.data.heatmaps.enabled, replay: parsed.data.replay.enabled, a11yScans: parsed.data.a11yScans.enabled });
              toaster.success({ title: "Research settings saved", description: "Live in your app within a minute." });
            } catch (err) {
              notifyError(err);
            }
          }}
        >
          <fieldset disabled={disabled} className="space-y-3">
            <legend className="text-[13px] font-semibold text-ink">Feedback</legend>
            <fieldset aria-describedby="feedback-platforms-hint" className="space-y-1.5">
              <legend className="text-sm text-ink">Accept feedback from</legend>
              <span id="feedback-platforms-hint" className="block text-xs text-ink-3">
                Where it's off, the SDK switches feedback off (your app can check <InlineCode>feedback.isEnabled</InlineCode> to hide its own button) and reports from that platform are refused.
              </span>
              <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1">
                {PLATFORMS.map((p) => (
                  <Checkbox
                    key={p}
                    className="min-h-6"
                    checked={s.feedback.platforms.includes(p)}
                    onCheckedChange={(on) => set("feedback", { platforms: on ? PLATFORMS.filter((x) => x === p || s.feedback.platforms.includes(x)) : s.feedback.platforms.filter((x) => x !== p) })}
                  >
                    {PLATFORM_LABEL[p]}
                    {p === app.platform && <span className="text-xs text-ink-3"> (this app)</span>}
                  </Checkbox>
                ))}
              </div>
            </fieldset>
            {s.feedback.platforms.includes("web") && (
              <>
                <Checkbox hint="People can report problems, confusion, ideas or accessibility barriers, with an optional annotated screenshot. Without the button, open the dialog from your own UI with metrickle.feedback.open()." checked={s.feedback.enabled} onCheckedChange={(v) => set("feedback", { enabled: v })}>Show a feedback button on web</Checkbox>
                {s.feedback.enabled && (
                  <div className="grid gap-3 pl-6 @xl:grid-cols-2">
                    <Field label="Button label">
                      <Input value={s.feedback.label} maxLength={24} onChange={(e) => set("feedback", { label: e.target.value })} />
                    </Field>
                    <Field label="Position">
                      <SelectField className="w-full" aria-label="Position" items={[{ value: "bottom-right", label: "Bottom right" }, { value: "bottom-left", label: "Bottom left" }]} value={s.feedback.position} onChange={(v) => set("feedback", { position: v as "bottom-right" | "bottom-left" })} />
                    </Field>
                    <div className="@xl:col-span-2">
                      <Checkbox hint="The browser asks the person for permission every time. They can hide parts before sending." checked={s.feedback.screenshots} onCheckedChange={(v) => set("feedback", { screenshots: v })}>Allow screenshots</Checkbox>
                    </div>
                  </div>
                )}
              </>
            )}
            <BrandingFields
              appId={app.id}
              branding={s.feedback.branding}
              logo={s.studyPages.logo}
              entitled={addons.data?.whiteLabel ?? false}
              onChange={(patch) => set("feedback", { branding: { ...s.feedback.branding, ...patch } })}
              // Logos save on upload, so they don't make the form dirty.
              onLogo={(logo) => setS((cur) => (cur ? { ...cur, studyPages: { ...cur.studyPages, logo } } : cur))}
            />
          </fieldset>

          <fieldset disabled={disabled} className="space-y-3">
            <legend className="text-[13px] font-semibold text-ink">Study pages</legend>
            <Field
              label="Name people see (optional)"
              hint={`Shown to people taking part in a study, on its pages, booking emails and calendar files. Leave empty to show your site's address${siteHost(app) ? `, ${siteHost(app)}` : ""}.`}
            >
              <Input value={s.studyPages.name ?? ""} maxLength={60} placeholder={siteHost(app) ?? undefined} onChange={(e) => set("studyPages", { name: e.target.value.trim() ? e.target.value : null })} />
            </Field>
          </fieldset>

          <fieldset disabled={disabled || !web} className="space-y-3">
            <legend className="text-[13px] font-semibold text-ink">Heatmaps</legend>
            <Checkbox hint="Adds a click event per click and a scroll event per pageview to your event volume." checked={s.heatmaps.enabled} onCheckedChange={(v) => set("heatmaps", { enabled: v })}>Record clicks and scroll depth</Checkbox>
            {s.heatmaps.enabled && (
              <div className="space-y-3 pl-6">
                <Percent label="Sessions to record" value={s.heatmaps.sampleRate} onChange={(v) => set("heatmaps", { sampleRate: v })} />
                <Checkbox
                  hint="Saves a copy of each page's layout, at most once a day per page and screen size, so heatmaps show the page under the clicks. Input values are always masked; use data-mk-block to leave an element out."
                  checked={s.heatmaps.snapshots}
                  onCheckedChange={(v) => set("heatmaps", { snapshots: v })}
                >
                  Capture page snapshots
                </Checkbox>
                {s.heatmaps.snapshots && (
                  <Field label="Mask snapshot text">
                    <Segmented aria-label="Mask snapshot text" items={[{ value: "all", label: "All text (recommended)" }, { value: "inputs", label: "Only marked elements" }]} value={s.heatmaps.maskText} onChange={(v) => set("heatmaps", { maskText: v as "all" | "inputs" })} />
                  </Field>
                )}
                {s.heatmaps.snapshots && (
                  <p className="text-xs text-ink-3">
                    {s.heatmaps.maskText === "all" ? (
                      <>A snapshot is one visitor's page, so names and orders on signed-in pages show as dots. Add <InlineCode>data-mk-unmask</InlineCode> to regions that are safe to show, such as navigation and headings.</>
                    ) : (
                      <>Page text is saved as one visitor saw it, except in elements marked <InlineCode>data-mk-mask</InlineCode>. Only use this on pages that show nothing personal.</>
                    )}
                  </p>
                )}
              </div>
            )}
          </fieldset>

          <fieldset disabled={disabled || !web} className="space-y-3">
            <legend className="text-[13px] font-semibold text-ink">Session replay</legend>
            <Checkbox hint="Every input value is always masked. Recordings are stored privately and erased with the person's data." checked={s.replay.enabled} onCheckedChange={(v) => set("replay", { enabled: v })}>Record sessions</Checkbox>
            {s.replay.enabled && (
              <div className="space-y-3 pl-6">
                <Percent label="Sessions to record" value={s.replay.sampleRate} onChange={(v) => set("replay", { sampleRate: v })} />
                <Field label="Mask page text">
                  <Segmented aria-label="Mask page text" items={[{ value: "all", label: "All text (recommended)" }, { value: "inputs", label: "Only marked elements" }]} value={s.replay.maskText} onChange={(v) => set("replay", { maskText: v as "all" | "inputs" })} />
                </Field>
                <p className="text-xs text-ink-3">
                  {s.replay.maskText === "all" ? (
                    <>All text shows as dots. Add <InlineCode>data-mk-unmask</InlineCode> to regions that are safe to show, such as navigation.</>
                  ) : (
                    <>Text is visible except in elements marked <InlineCode>data-mk-mask</InlineCode>. Use <InlineCode>data-mk-block</InlineCode> to leave an element out entirely.</>
                  )}
                </p>
                <Checkbox
                  hint={<>Recommended. Recording starts after your consent banner calls <InlineCode>metrickle.consent({"{ replay: true }"})</InlineCode>. Cookieless installs never record.</>}
                  checked={s.replay.requireConsent}
                  onCheckedChange={(v) => set("replay", { requireConsent: v })}
                >
                  Wait for consent
                </Checkbox>
              </div>
            )}
          </fieldset>

          <fieldset disabled={disabled || !web} className="space-y-3">
            <legend className="text-[13px] font-semibold text-ink">Accessibility scans</legend>
            <Checkbox
              hint="For pages your CI never visits, such as signed-in pages. On a share of page views, the SDK checks the page against WCAG A and AA in idle time, at most once a day per page, and sends only the rules that fail and each element's tag, id, class, name, type and role. Never page text, input values or who was visiting. Pages your CI scans keep CI's results."
              checked={s.a11yScans.enabled}
              onCheckedChange={(v) => set("a11yScans", { enabled: v })}
            >
              Check pages in visitors' browsers
            </Checkbox>
            {s.a11yScans.enabled && (
              <div className="space-y-3 pl-6">
                <Percent label="Page views that may check their page" value={s.a11yScans.sampleRate} onChange={(v) => set("a11yScans", { sampleRate: v })} />
                <p className="text-xs text-ink-3">
                  The checker (axe-core, about 165 KB gzipped) loads only when a chosen page view's page is due a check, and never for visitors with Save-Data on. Cookieless installs keep nothing about it in the browser. Leave an element out with <InlineCode>data-mk-block</InlineCode>. Paths with ids in them, like <InlineCode>/orders/8812</InlineCode>, are filed as <InlineCode>/orders/:id</InlineCode>.
                </p>
              </div>
            )}
          </fieldset>

          <Button variant="primary" disabled={disabled} pending={save.isPending} pendingLabel="Saving…">
            Save research settings
          </Button>
        </form>
      </CardSection>
    </Card>
  );
}

/**
 * White-label options, locked unless the workspace has the add-on. The API enforces this too: it
 * ignores branding changes without it and never serves saved branding once the add-on ends.
 */
function BrandingFields({ appId, branding, logo, entitled, onChange, onLogo }: {
  appId: string;
  branding: Branding;
  logo: StudyPages["logo"];
  entitled: boolean;
  onChange: (patch: Partial<Branding>) => void;
  onLogo: (logo: StudyPages["logo"]) => void;
}) {
  const [hex, setHex] = useState(branding.accent ?? "");
  useEffect(() => setHex(branding.accent ?? ""), [branding.accent]);
  const valid = /^#[0-9a-fA-F]{6}$/.test(hex);
  const lowContrast = valid && WIDGET_BACKGROUNDS.some((bg) => contrastRatio(hex, bg) < 3);
  const invalid = hex !== "" && !valid;
  const hintId = "branding-hint";
  const errorId = "brand-colour-error";
  return (
    <fieldset disabled={!entitled} aria-describedby={hintId} className="space-y-3 rounded-lg border border-line p-3">
      <legend className="px-1 text-[13px] font-semibold text-ink">
        Branding{!entitled && <Badge className="ml-2">Agency plan or add-on</Badge>}
      </legend>
      <p id={hintId} className="text-xs text-ink-3">
        {entitled ? (
          "Make the widget, surveys and study pages look like part of your product."
        ) : (
          <>
            Hide Metrickle branding and use your own colour. White-label comes with Agency and Enterprise; on other plans it's an add-on we switch on by request.{" "}
            <a className="text-accent-text underline" href="mailto:chris@metrickle.com?subject=White-label%20add-on">
              Ask about the add-on
            </a>
            .
          </>
        )}
      </p>
      <Checkbox hint="Also shows the Metrickle logo in the feedback dialog and at the top of study pages." checked={entitled ? branding.poweredBy : true} onCheckedChange={(v) => onChange({ poweredBy: v })}>Show "Powered by Metrickle"</Checkbox>
      <div role="group" aria-labelledby="brand-colour-label" aria-describedby="brand-colour-hint" className="space-y-1.5">
        <span id="brand-colour-label" className="block text-[13px] font-medium text-ink">Brand colour</span>
        <div className="flex items-center gap-2">
          <input
            type="color"
            aria-label="Pick brand colour"
            className="h-9 w-11 shrink-0 cursor-pointer rounded border border-control bg-surface disabled:cursor-not-allowed"
            value={valid ? hex : WIDGET_DEFAULT_ACCENT}
            onChange={(e) => {
              setHex(e.target.value);
              onChange({ accent: e.target.value.toLowerCase() });
            }}
          />
          <Input
            className="w-32 font-mono"
            aria-label="Brand colour hex code"
            placeholder={WIDGET_DEFAULT_ACCENT}
            maxLength={7}
            value={entitled ? hex : ""}
            aria-invalid={entitled && invalid}
            aria-describedby={entitled && invalid ? errorId : undefined}
            onChange={(e) => {
              const v = e.target.value.trim();
              setHex(v);
              if (v === "") onChange({ accent: null });
              else if (/^#[0-9a-fA-F]{6}$/.test(v)) onChange({ accent: v.toLowerCase() });
            }}
          />
          {entitled && branding.accent && (
            <Button type="button" size="sm" variant="ghost" onClick={() => { setHex(""); onChange({ accent: null }); }}>
              Reset
            </Button>
          )}
        </div>
        <span id="brand-colour-hint" className="block text-xs text-ink-3">
          Used for buttons and selected answers in the widget, surveys and study pages. Text on it is set to black or white automatically for contrast. Leave empty for Metrickle blue.
        </span>
      </div>
      {entitled && invalid && <FieldError id={errorId}>Use a 6-digit hex code, like {WIDGET_DEFAULT_ACCENT}.</FieldError>}
      {entitled && lowContrast && (
        <p className="text-xs text-ink-2">
          This colour is hard to see against a light or dark page, so the widget and study pages keep their own focus outline and check marks to show what's selected.
        </p>
      )}
      <div role="group" aria-labelledby="logo-label" aria-describedby="logo-hint" className="space-y-2">
        <span id="logo-label" className="block text-[13px] font-medium text-ink">Study page logo</span>
        <span id="logo-hint" className="block text-xs text-ink-3">
          Shown at the top of study pages in place of the name, and read out as the name. PNG, WebP or SVG, up to {LOGO_MAX_BYTES / 1000} KB; it's shown 32px tall. Without a dark version, the logo sits on a light tile in dark mode.
        </span>
        <LogoSlot appId={appId} theme="light" label="Logo" file={logo.light} entitled={entitled} onSaved={onLogo} />
        <LogoSlot appId={appId} theme="dark" label="Dark mode version (optional)" file={logo.dark} entitled={entitled} onSaved={onLogo} />
      </div>
    </fieldset>
  );
}

/** The site's address, as study pages show it when no name is set. */
function siteHost(app: App): string | null {
  try {
    return app.domains[0] ? new URL(app.domains[0]).hostname.replace(/^www\./, "") : null;
  } catch {
    return null;
  }
}

/** One logo: a preview, then upload or replace, and remove. Each change saves straight away. */
function LogoSlot({ appId, theme, label, file, entitled, onSaved }: {
  appId: string;
  theme: "light" | "dark";
  label: string;
  file: string | null;
  entitled: boolean;
  onSaved: (logo: StudyPages["logo"]) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const { confirm, dialog } = useConfirm();
  const [busy, setBusy] = useState<"upload" | "remove" | null>(null);
  const run = async (kind: "upload" | "remove", req: () => Promise<ResearchSettings>, title: string) => {
    setBusy(kind);
    try {
      onSaved((await req()).studyPages.logo);
      toaster.success({ title });
    } catch (err) {
      notifyError(err);
    } finally {
      setBusy(null);
    }
  };
  const path = `/apps/${appId}/research/logo/${theme}`;
  const name = theme === "dark" ? "dark mode logo" : "logo";
  return (
    <div className="space-y-1.5">
      <span className="block text-[13px] text-ink">{label}</span>
      <div className="flex flex-wrap items-center gap-2">
        {/* The light logo is previewed on white, as a light study page shows it, whatever the dashboard's theme. */}
        <div className={`flex h-12 w-40 shrink-0 items-center justify-center rounded-lg border border-line ${theme === "light" && file ? "bg-page-canvas" : "bg-surface-2"}`}>
          {file ? <img src={`/r/logo/${encodeURIComponent(appId)}/${file}`} alt="" className="max-h-8 max-w-36 object-contain" /> : <span className="text-xs text-ink-3">None</span>}
        </div>
        <input
          ref={input}
          type="file"
          hidden
          accept="image/png,image/webp,image/svg+xml"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) void run("upload", () => api<ResearchSettings>(path, { method: "PUT", body: f, headers: { "content-type": f.type || "application/octet-stream" } }), "Logo saved");
          }}
        />
        <Button type="button" size="sm" disabled={!entitled} pending={busy === "upload"} pendingLabel="Uploading…" onClick={() => input.current?.click()}>
          <ImageUp size={14} aria-hidden /> {file ? `Replace ${name}` : `Upload ${name}`}
        </Button>
        {file && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            pending={busy === "remove"}
            pendingLabel="Removing…"
            onClick={async () => {
              if (await confirm({ title: `Remove the ${name}?`, description: "Study pages show the name instead. You can upload it again later.", confirmLabel: "Remove" })) {
                void run("remove", () => api<ResearchSettings>(path, { method: "DELETE" }), "Logo removed");
              }
            }}
          >
            <Trash2 size={14} aria-hidden /> Remove<span className="sr-only"> {name}</span>
          </Button>
        )}
      </div>
      {dialog}
    </div>
  );
}

function Percent({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <Field label={`${label} (%)`}>
      <Input type="number" className="w-28" min={0} max={100} step={1} value={Math.round(value * 100)} onChange={(e) => onChange(Math.max(0, Math.min(100, Number(e.target.value) || 0)) / 100)} />
    </Field>
  );
}
